import Swiper from './swiper.min.js';

/**
 * Theme — Tag Slider  (component-tag-slider.js)
 *
 * A lightweight Swiper wrapper for variable-width chip rows (e.g. the blog
 * tag filter). Unlike <grid-slider>, which lays out fixed-width cards with a
 * numeric slidesPerView, this uses `slidesPerView: 'auto'` + freeMode so each
 * pill keeps its natural width and the row scrolls smoothly with momentum.
 *
 * Prev/next arrows are revealed only when the chips actually overflow; when
 * they all fit, Swiper locks and the arrows hide (watchOverflow).
 *
 * ──────────────────────────────────────────────────────────────────
 * MARKUP CONTRACT
 * ──────────────────────────────────────────────────────────────────
 *   <tag-slider data-a11y-prev="…" data-a11y-next="…">
 *     <button class="…__btn …__btn--prev" type="button" hidden>…</button>
 *     <div class="…__viewport">
 *       <ul class="main-blog__tags" role="list">
 *         <li class="main-blog__tag-slide"><a class="main-blog__tag">…</a></li>
 *       </ul>
 *     </div>
 *     <button class="…__btn …__btn--next" type="button" hidden>…</button>
 *   </tag-slider>
 *
 * FALLBACK: without JS the viewport scrolls horizontally (CSS), so the tags
 * stay fully usable before/without Swiper.
 */

class TagSlider extends HTMLElement {
  constructor() {
    super();
    /** @type {import('./swiper.min.js').default | null} */
    this.swiper = null;
    this.viewport = null;
    this.prevBtn = null;
    this.nextBtn = null;
    this.sectionRoot = null;

    this._onSectionLoad = this._onSectionLoad.bind(this);
    this._onLockChange = this._onLockChange.bind(this);
  }

  connectedCallback() {
    this._cacheRefs();
    this.sectionRoot = this.closest('.shopify-section') || null;
    this._a11yPrev = this.dataset.a11yPrev || 'Previous';
    this._a11yNext = this.dataset.a11yNext || 'Next';

    if (!this.viewport) return;

    this._init();
    document.addEventListener('shopify:section:load', this._onSectionLoad);
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:section:load', this._onSectionLoad);
    this._destroy();
  }

  _cacheRefs() {
    this.viewport = this.querySelector('.main-blog__tagslider-viewport');
    this.prevBtn = this.querySelector('.main-blog__tagslider-btn--prev');
    this.nextBtn = this.querySelector('.main-blog__tagslider-btn--next');
  }

  _init() {
    if (this.swiper || !Swiper || !this.viewport) return;

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const navConfig =
      this.prevBtn && this.nextBtn
        ? { prevEl: this.prevBtn, nextEl: this.nextBtn, disabledClass: 'is-disabled' }
        : false;

    this.swiper = new Swiper(this.viewport, {
      slidesPerView: 'auto',
      spaceBetween: 8,
      freeMode: { enabled: true, sticky: false, momentum: true },
      speed: prefersReduced ? 0 : 350,
      threshold: 6,
      grabCursor: !prefersReduced,
      watchOverflow: true,
      resistanceRatio: 0.5,

      // Custom class names so the existing markup/styles are reused.
      wrapperClass: 'main-blog__tags',
      slideClass: 'main-blog__tag-slide',

      a11y: {
        enabled: true,
        prevSlideMessage: this._a11yPrev,
        nextSlideMessage: this._a11yNext,
        notificationClass: 'visually-hidden',
      },

      navigation: navConfig,

      on: {
        lock: this._onLockChange,
        unlock: this._onLockChange,
        resize: this._onLockChange,
      },
    });

    this.classList.add('is-active');
    // isLocked is resolved synchronously after construction.
    this._onLockChange();
  }

  _onLockChange() {
    const locked = this.swiper ? this.swiper.isLocked : true;
    if (this.prevBtn) this.prevBtn.hidden = locked;
    if (this.nextBtn) this.nextBtn.hidden = locked;
    this.classList.toggle('is-locked', locked);
  }

  _destroy() {
    if (!this.swiper) return;
    this.swiper.destroy(true, true);
    this.swiper = null;
    this.classList.remove('is-active', 'is-locked');
    if (this.prevBtn) this.prevBtn.hidden = true;
    if (this.nextBtn) this.nextBtn.hidden = true;
  }

  _onSectionLoad(event) {
    if (!this.sectionRoot) return;
    const target = event.target;
    if (target && target.contains && target.contains(this)) {
      this._destroy();
      this._cacheRefs();
      this._init();
    }
  }
}

if (!customElements.get('tag-slider')) {
  customElements.define('tag-slider', TagSlider);
}
